/**
 * E2E renderer test probe — drives the REAL download-start dual-emit with the
 * WEB surface's origin, via `globalThis.__irisDownloadOriginProbe`.
 *
 * WHY a probe is required here (read before replacing it with a UI interaction):
 * the origin resolver branches on `isElectronApp()`, which is computed ONCE at
 * module load from `window.electron` (electron/utils.ts) and cached. The Playwright
 * e2e suite only ever drives an Electron renderer, where that flag is permanently
 * true, so no navigation or click inside the suite can reach the web branch —
 * deleting `window.electron` at runtime would not flip the cached flag, it would
 * just break the app. `resolveDownloadOrigin(surface)` therefore takes the surface
 * as an explicit trailing parameter (defaulted to the resolver, never passed by
 * product code) and this probe supplies the web value.
 *
 * What the probe does NOT fake: the origin map lookup, the domain tracker's
 * dual-write, `track()`, event validation, envelope enrichment, the before_send
 * chain, and the ingest POST are all the real production path — the same
 * `track()` the analytics React context hands components (analytics/context.tsx
 * passes this exact function). The ONLY injected value is the surface constant;
 * the surface RESOLUTION is covered by the jest suites
 * (analytics/domains/download-lifecycle.test.ts and each vendor's own
 * download.test.tsx under routes/agent/setup/vendor).
 *
 * The synthetic anchor `link.click()` the product fires right AFTER the emit is
 * deliberately not reproduced: it is the browser download itself, not telemetry,
 * and in Electron it would raise a native save dialog.
 *
 * Per `think-in-systems` (ai.knowledge/global): a primitive. It exposes the emit
 * the receipt asserts on, not a re-implementation of it.
 * Per `stay-in-scope` (ai.knowledge/global): one method, for the one origin that
 * has no other route to a send boundary. The Electron origin is already asserted
 * through the real product trigger in the same spec.
 *
 * Mirrors calibration-probe.ts exactly: same `window.electron.process.env` gate
 * (bare `process.env` is EMPTY in this renderer — see that file's long comment),
 * same single-statement global assignment, same test-only intent.
 */
import { config } from '#~/config/config.js';
import { track } from '#~/analytics/track.js';
import type { AnalyticsEvent } from '#~/analytics/types/events.js';
import { DOWNLOAD_SURFACES } from '#~/analytics/types/properties.js';
import type { DownloadOrigin } from '#~/analytics/types/properties.js';
import type { AnalyticsTrackFn } from '#~/analytics/domains/types.js';
import {
  createDownloadLifecycleTracker,
  resolveDownloadOrigin
} from '#~/analytics/domains/download-lifecycle.js';

interface DownloadOriginProbe {
  /**
   * @returns the origin actually emitted, so the spec can prove the web branch
   *          ran rather than inferring it from a passing wire assertion. Null
   *          when the env/vendor context is absent, which is the same guard the
   *          product call sites emit behind — a silent no-op would otherwise
   *          look identical to a dropped property at the boundary.
   */
  startWebDownload(): DownloadOrigin | null;
}

// Renderer-process gate. See calibration-probe.ts for why this reads through the
// preload bridge rather than bare `process.env`.
const isE2eEnabled = (): boolean => {
  try {
    return (
      (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } })
        .window?.electron?.process?.env?.IRIS_E2E_TEST === '1'
    );
  } catch {
    return false;
  }
};

/**
 * The (name, category, properties) shape every domain tracker takes, bound to the
 * module-level `track`. This is the same three-line adapter `useAnalytics` builds
 * over the identical `track` function (analytics/hooks/use-analytics.ts), repeated
 * here because a hook cannot be called outside a React tree. The `as` mirrors that
 * hook's: `AnalyticsEvent` is a union that couples each name to its category, so a
 * caller holding a widened `EventName` cannot narrow to a member.
 */
const trackDomainEvent: AnalyticsTrackFn = (name, category, properties) => {
  void track({ name, category, properties, timestamp: Date.now() } as AnalyticsEvent);
};

if (isE2eEnabled()) {
  const probe: DownloadOriginProbe = {
    /**
     * Initiate a download exactly as the shared agent-setup <Download> page does
     * when it renders in a browser: resolve the web origin, then dual-emit
     * through the download-lifecycle tracker with the same env/vendor context
     * and behind the same guard each vendor's `download.tsx` emits behind.
     */
    startWebDownload(): DownloadOrigin | null {
      if (!config.env || !config.vendor) {
        return null;
      }

      const origin = resolveDownloadOrigin(DOWNLOAD_SURFACES.WEB);

      createDownloadLifecycleTracker(trackDomainEvent).trackDownloadAppStarted({
        ...origin,
        environment: config.env,
        vendor: config.vendor
      });

      return origin;
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__irisDownloadOriginProbe = probe;
}
